import { Injectable } from '@angular/core';
import { IOrganisationsHierarchy } from './organisation-data.service';
import { HttpClient } from '@angular/common/http';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import { AssetDataPoint, DataPoint } from './datapoints-data.service';
import { OfflineEvent, RuleEvent } from './events-data.service';
import { Action } from './actions-data.service';

export interface IModelEdit {
	/** ModelEditID */
	ModelEditID: number;
	/** ModelType */
	ModelType: number;
	/** ModelID */
	ModelID: number;
	/** PreviousProperties */
	PreviousProperties: string;
	/** NewProperties */
	NewProperties: string;
	/** UserSub */
	UserSub: string;
	/** Timestamp */
	Timestamp: number;
}

export class ModelEdit {
	/**
	 * Default Constructor
	 * @param ModelEditID ModelEditID 
	 * @param ModelType ModelType 
	 * @param ModelID ModelID 
	 * @param PreviousProperties PreviousProperties  
	 * @param NewProperties NewProperties 
	 * @param UserSub UserSub 
	 * @param Timestamp Timestamp 
	 */
	constructor(
		/** ModelEditID */
		public ModelEditID: number,
		/** ModelType */
		public ModelType: number,
		/** ModelID */
		public ModelID: number,
		/** PreviousProperties */
		public PreviousProperties: string,
		/** NewProperties */
		public NewProperties: string,
		/** UserSub */
		public UserSub: string,
		/** Timestamp */
		public Timestamp: number,
	) { }
}

export class CreateModelData {
	data: any;
	tolerance: number;
	sensitivity: number;
	isAutomatic: boolean = false;
	remind?: boolean = false;
	remindAfter?: number = 0;
	automaticWindowWidth?: number = 0;
	recreationFrequency?: number = 0;
	overlapPeriod?: number = 0;
	datapointIDs?: string = "";
}

export class TestModelData {
	/**
	 * Default Constructor
	 * @param usingTimeRange usingTimeRange 
	 * @param numberOfDays numberOfDays 
	 * @param start start 
	 * @param end end 
	 */
	constructor(
		public usingTimeRange: boolean = false,
		public numberOfDays: number = 7,
		public start: number = 0,
		public end: number = 0
	) { }
}

export interface IModelType {
	/** Model Type ID */
	ModelTypeID: number;
	/** Model Type Name */
	ModelName: string;
	/** Model Type Description */
	ModelDescription: string;
	/** Model Use */
	ModelUse: string;
}

export class ModelType {
	/**
	 * Default Constructor
	 * @param ModelTypeID ModelTypeID 
	 * @param ModelName ModelName 
	 * @param ModelDescription ModelDescription
	 * @param ModelUse ModelUse 
	 */
	constructor(
		/** Model Type ID */
		public ModelTypeID: number,
		/** Model Type Name */
		public ModelName: string,
		/** Model Type Description */
		public ModelDescription: string,
		/** Model Use */
		public ModelUse: string
	) { }
}

export interface IAssetDistribution {
	/** AssetDistributionID */
	AssetDistributionID: number;
	/** AssetID */
	AssetID: number;
	/** Distribution */
	Distribution: any;
	/** CreatedAt */
	CreatedAt: number;
	/** MostRecent */
	MostRecent: boolean;
	/** StartPoint */
	StartPoint: number;
	/** EndPoint */
	EndPoint: number;
	/** IsAutomatic */
	IsAutomatic: boolean;
	/** AutomaticWindowWidth */
	AutomaticWindowWidth: number;
	/** Tolerance */
	Tolerance: number;
	/** Sensitivity */
	Sensitivity: number;
	/** RecreationFrequency */
	RecreationFrequency: number;
	/** OverlapPeriod */
	OverlapPeriod: number;
	/** DatapointIds */
	DatapointIDs: any;
	/** CreatedByUserSub */
	CreatedByUserSub: any;
}

export class AssetDistribution implements IAssetDistribution {
	/**
	 * Default Constructor
	 * @param AssetDistributionID AssetDistributionID 
	 * @param AssetID AssetID 
	 * @param Distribution Distribution 
	 * @param CreatedAt CreatedAt 
	 * @param MostRecent MostRecent 
	 * @param StartPoint StartPoint 
	 * @param EndPoint EndPoint 
	 * @param IsAutomatic IsAutomatic 
	 * @param AutomaticWindowWidth AutomaticWindowWidth 
	 * @param Tolerance Tolerance
	 * @param Sensitivity Sensitivity
	 * @param RecreationFrequency RecreationFrequency 
	 * @param OverlapPeriod OverlapPeriod 
	 * @param DatapointIDs DatapointIDs 
	 * @param CreatedByUserSub CreatedByUserSub 
	 */
	constructor(
		public AssetDistributionID: number,
		public AssetID: number,
		public Distribution: any,
		public CreatedAt: number,
		public MostRecent: boolean,
		public StartPoint: number,
		public EndPoint: number,
		public IsAutomatic: boolean,
		public AutomaticWindowWidth: number,
		public Tolerance: number,
		public Sensitivity: number,
		public RecreationFrequency: number,
		public OverlapPeriod: number,
		public DatapointIDs: any,
		public CreatedByUserSub: string,
		public AssetName?: string,
		public Disabled?: boolean,
	) { }
}

/**
 * Class
 */
@Injectable({
	providedIn: 'root'
})
export class ModelsService {

	/** apiModels */
	private apiModels = "api/{0}/organisation/{1}/models";

	/**
   * Default Constructor
   * @param http Imports the 'HttpClient' Object for internet requests
   * @param globalFunctions Global Functions
   * @param siteSubscriptions site Subscriptions
   */
	constructor(
		private http: HttpClient,
		public globalFunctions: GlobalFunctions,
		public siteSubscriptions: siteSubscriptions
	) { }

	/**
	 * postAssetDistribution
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param assetID assetID 
	 * @param distributionData distributionData  
	 * @returns 
	 */
	postAssetDistribution$(AccountGUID: string, orgUID: string, assetID: number, inputData: CreateModelData): Observable<any> {

		let info = {
			isAutomatic: inputData.isAutomatic,
			automaticWindowWidth: inputData.automaticWindowWidth,
			remind: inputData.remindAfter ? true : false,
			remindAfter: inputData.remindAfter ? inputData.remindAfter : 0,
			tolerance: inputData.tolerance,
			sensitivity: inputData.sensitivity,
			recreationFrequency: inputData.recreationFrequency ? inputData.recreationFrequency : 0,
			overlapPeriod: inputData.overlapPeriod ? inputData.overlapPeriod : 0,
			datapointIDs: inputData.datapointIDs ? inputData.datapointIDs : ""
		};
		let input = {
			inputData: inputData.data,
			info: info
		}

		const data = JSON.stringify(input);

		let url = this.apiModels;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/distributions/${assetID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * testAssetDistribution
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param distribution distribution 
	 * @returns 
	 */
	testAssetDistribution$(AccountGUID: string, orgUID: string, distribution: AssetDistribution, timeData: TestModelData): Observable<any> {
		const data = JSON.stringify(distribution);

		let url = this.apiModels;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/distributions/test`;

		if (!timeData.usingTimeRange) {
			url += `?numberOfDays=${timeData.numberOfDays}`;
		} else {
			url += `?start=${timeData.start}&end=${timeData.end}`;
		}

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * testAssetDistributionAuto
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param distribution distribution 
	 * @param start start 
	 * @param end end 
	 * @returns 
	 */
	testAssetDistributionAuto$(AccountGUID: string, orgUID: string, distribution: AssetDistribution, start: number,
		treatAsAutomatic: boolean, numberOfWeeks: number, recreationFrequency: number, overlapPeriod: number): Observable<any> {
		const data = JSON.stringify(distribution);

		let url = this.apiModels;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/distributions/test/auto`;

		url += `?start=${start}&treatAsAutomatic=${treatAsAutomatic}&numberOfWeeks=${numberOfWeeks}&recreationFrequency=${recreationFrequency}&overlapPeriod=${overlapPeriod}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * Get Asset Distribution
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param assetID assetID 
	 * @param distributionID distributionID 
	 * @param onlyMostRecent onlyMostRecent 
	 * @returns 
	 */
	getAssetDistribution$(AccountGUID: string, orgUID: string, assetID: number = 0, distributionID: number = 0, onlyMostRecent: boolean = false): Observable<any> {
		const data = {};

		let url = this.apiModels;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/distributions`;

		url += `?assetID=${assetID}&distributionID=${distributionID}&onlyMostRecent=${onlyMostRecent}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * getModelTypes
	 * @param AccountGUID AccountGUID  
	 * @param orgUID orgUID 
	 * @returns 
	 */
	getModelTypes$(AccountGUID: string, orgUID: string): Observable<any> {
		const data = {};

		let url = this.apiModels;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/modelTypes`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * patchDistribution
	 * @param AccountGUID AccountGUID  
	 * @param orgUID orgUID 
	 * @param distributionID distributionID  
	 * @param tolerance tolerance 
	 * @param sensitivity sensitivity 
	 * @param recreationFrequency recreationFrequency 
	 * @param overlapPeriod overlapPeriod 
	 * @returns 
	 */
	patchDistribution$(AccountGUID: string, orgUID: string, distributionID: number, tolerance: number, sensitivity: number,
		recreationFrequency: number, overlapPeriod: number): Observable<any> {

		const data = {
			Tolerance: tolerance,
			Sensitivity: sensitivity,
			RecreationFrequency: recreationFrequency,
			OverlapPeriod: overlapPeriod
		};

		let url = this.apiModels;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${distributionID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * patchOldDistribution
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param distributionID distributionID  
	 * @param assetID assetID 
	 * @param active active 
	 * @param disabled disabled 
	 * @returns 
	 */
	patchOldDistribution$(AccountGUID: string, orgUID: string, distributionID: number, assetID: number, active: boolean, disabled: boolean): Observable<any> {
		const data = {};

		let url = this.apiModels;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/old/${assetID}/${distributionID}`;

		url += `?active=${active}&disabled=${disabled}`;

		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * getModelEdits
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param modelType modelType 
	 * @param modelID modelID 
	 * @returns 
	 */
	getModelEdits$(AccountGUID: string, orgUID: string, modelType: number, modelID: number): Observable<any> {
		const data = {};

		let url = this.apiModels;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${modelType}/${modelID}/edits`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}
}